import React, { useCallback, useEffect, useRef, useState } from 'react';

import { type BoardOperation, type DropSide, dropOperation, dropSide } from './editor';
import { splitBlockId } from './localOperations';
import type { BoardEditor } from './useBoardEdit';

type Target = { id: string; side: DropSide } | { tab: string };

export type BlockDrag = { id: string; title: string; label: string; target?: Target };

type MovingDrag = BlockDrag & { x: number; y: number };

type Point = { clientX: number; clientY: number };

type Pending = { id: string; title: string; x: number; y: number; started: boolean; touch?: { held: boolean; timer: number; release: AbortController } };

const START_PX = 5;
const HOLD_MS = 350;
const TAB_HOVER_MS = 400;
const EDGE_PX = 56;
const SCROLL_PX = 14;

function sameTarget(left: Target | undefined, right: Target | undefined): boolean {
  if (left === right) return true;
  if (!left || !right) return false;
  return 'tab' in left ? 'tab' in right && left.tab === right.tab : 'id' in right && left.id === right.id && left.side === right.side;
}

function semanticsChanged(before: BlockDrag | undefined, next: BlockDrag | undefined): boolean {
  return before?.id !== next?.id || before?.title !== next?.title || before?.label !== next?.label || !sameTarget(before?.target, next?.target);
}

function positionPreview(node: HTMLDivElement, drag: MovingDrag) {
  node.style.transform = `translate3d(${drag.x + 14}px, ${drag.y + 14}px, 0)`;
}

function scrollParent(node: HTMLElement): HTMLElement | undefined {
  for (let at = node.parentElement; at; at = at.parentElement) {
    if (/(auto|scroll)/.test(getComputedStyle(at).overflowY) && at.scrollHeight > at.clientHeight) return at;
  }
  return undefined;
}

const textOf = (node: Element | null | undefined) => node?.textContent?.trim() || undefined;

/** Names the insertion point from the page itself, so the label matches what the person sees. */
function dropLabel(target: Target | undefined, dragged: string, host: HTMLElement, title: string): string {
  if (!target) return title;
  if ('tab' in target) return `Move to ${textOf(document.querySelector(`[data-lens-tab="${CSS.escape(target.tab)}"]`)) ?? 'that tab'}`;
  const [blockId, item] = target.id.split('/');
  const block = [...host.querySelectorAll<HTMLElement>('[data-lens-block]')].find((entry) => entry.dataset.lensBlock === blockId);
  const nameOf = (entry: HTMLElement | undefined) => textOf(entry?.querySelector('.bh h3, h3')) ?? 'the block above';
  if (item) {
    const tile = block?.querySelector(`[data-lens-item="${CSS.escape(item)}"] .l`);
    return `Move ${target.side === 'before' ? 'before' : 'after'} ${textOf(tile) ?? item}`;
  }
  if (target.side === 'after' || target.side === 'below') return `Move after ${nameOf(block)}`;
  const all = [...host.querySelectorAll<HTMLElement>('[data-lens-block]')].filter((entry) => entry.dataset.lensBlock !== dragged);
  const before = all[all.indexOf(block as HTMLElement) - 1];
  return before ? `Move after ${nameOf(before)}` : 'Move to the start';
}

export function dragOperation(drag: BlockDrag | undefined): BoardOperation | undefined {
  if (!drag?.target) return undefined;
  return 'tab' in drag.target
    ? { op: 'move', id: drag.id, tab: drag.target.tab }
    : dropOperation(drag.id, drag.target.id, drag.target.side);
}

function hitAt(under: HTMLElement | null, dragged: string): { id: string; box: DOMRect; stacked: boolean } | undefined {
  const tile = dragged.includes('/') ? under?.closest<HTMLElement>('.figs [data-lens-item]') : null;
  const block = (tile ?? under)?.closest<HTMLElement>('[data-lens-block]');
  if (!block) return undefined;
  const id = tile ? `${block.dataset.lensBlock}/${tile.dataset.lensItem}` : block.dataset.lensBlock!;
  if (id === dragged || id === splitBlockId(dragged) || (!tile && dragged.startsWith(`${id}/`)) || block.parentElement?.closest(`[data-lens-block="${CSS.escape(dragged)}"]`)) return undefined;
  const row = block.closest<HTMLElement>('.row');
  return { id, box: (tile ?? block).getBoundingClientRect(), stacked: row ? getComputedStyle(row).gridTemplateColumns.split(' ').length === 1 : false };
}

export default function useBlockDrag(
  editor: BoardEditor | undefined,
  root: React.RefObject<HTMLElement>,
  onTab: (tabId: string) => void,
) {
  const [drag, setDrag] = useState<BlockDrag>();
  const pending = useRef<Pending | undefined>(undefined);
  const touchHandlers = useRef<{ slide: (event: TouchEvent) => void; lift: (event: TouchEvent) => void }>(undefined);
  const current = useRef<MovingDrag | undefined>(undefined);
  const preview = useRef<HTMLDivElement | null>(null);
  const frame = useRef<number | undefined>(undefined);
  const tabTimer = useRef<{ tab: string; timer: number } | undefined>(undefined);
  const showTab = useRef(onTab);
  showTab.current = onTab;

  const attachPreview = useCallback((node: HTMLDivElement | null) => {
    preview.current = node;
    if (node && current.current) positionPreview(node, current.current);
  }, []);

  const update = (next: MovingDrag | undefined) => {
    const before = current.current;
    current.current = next;
    if (!next && frame.current !== undefined) {
      window.cancelAnimationFrame(frame.current);
      frame.current = undefined;
    } else if (next && frame.current === undefined) {
      frame.current = window.requestAnimationFrame(() => {
        frame.current = undefined;
        if (preview.current && current.current) positionPreview(preview.current, current.current);
      });
    }
    if (semanticsChanged(before, next))
      setDrag(next ? { id: next.id, title: next.title, label: next.label, target: next.target } : undefined);
  };

  useEffect(() => () => {
    if (frame.current !== undefined) window.cancelAnimationFrame(frame.current);
  }, []);

  useEffect(() => {
    const clearTab = () => {
      if (tabTimer.current) window.clearTimeout(tabTimer.current.timer);
      tabTimer.current = undefined;
    };
    const scrolling: { node?: HTMLElement; speed: number; frame?: number } = { speed: 0 };
    const stopScrolling = () => {
      if (scrolling.frame) window.cancelAnimationFrame(scrolling.frame);
      scrolling.frame = undefined;
    };
    const scrollNear = (event: Point, host: HTMLElement) => {
      scrolling.node ??= scrollParent(host);
      const edge = scrolling.node?.getBoundingClientRect();
      scrolling.speed = !edge ? 0 : event.clientY < edge.top + EDGE_PX ? -SCROLL_PX : event.clientY > edge.bottom - EDGE_PX ? SCROLL_PX : 0;
      if (!scrolling.speed) return stopScrolling();
      if (scrolling.frame) return;
      const step = () => {
        scrolling.node?.scrollBy(0, scrolling.speed);
        scrolling.frame = window.requestAnimationFrame(step);
      };
      scrolling.frame = window.requestAnimationFrame(step);
    };
    const finish = (apply: boolean) => {
      const operation = apply ? dragOperation(current.current) : undefined;
      if (pending.current?.touch) {
        window.clearTimeout(pending.current.touch.timer);
        pending.current.touch.release.abort();
      }
      pending.current = undefined;
      stopScrolling();
      scrolling.node = undefined;
      clearTab();
      update(undefined);
      if (operation && editor) editor.apply(operation);
    };
    const move = (event: Point) => {
      const at = pending.current;
      if (!at) return;
      const travel = Math.hypot(event.clientX - at.x, event.clientY - at.y);
      if (at.touch && !at.touch.held) {
        if (travel >= START_PX) finish(false);
        return;
      }
      if (!at.started && travel < START_PX) return;
      at.started = true;
      const under = document.elementFromPoint(event.clientX, event.clientY) as HTMLElement | null;
      const tab = under?.closest<HTMLElement>('[data-lens-tab]')?.dataset.lensTab;
      if (tab && tabTimer.current?.tab !== tab) {
        clearTab();
        tabTimer.current = {
          tab,
          timer: window.setTimeout(() => showTab.current(tab), TAB_HOVER_MS),
        };
      } else if (!tab) clearTab();
      const host = root.current;
      if (!host) return;
      scrollNear(event, host);
      const origin = host.getBoundingClientRect();
      const inside = event.clientX >= origin.left && event.clientX <= origin.right && event.clientY >= origin.top && event.clientY <= origin.bottom;
      const over = inside ? hitAt(under, at.id) : undefined;
      let target = current.current?.target;
      if (tab) target = { tab };
      else if (!inside) target = undefined;
      else if (over?.id.includes('/')) target = { id: over.id, side: event.clientX < over.box.left + over.box.width / 2 ? 'before' : 'after' };
      else if (over) target = { id: over.id, side: dropSide(over.box, event.clientX, event.clientY, over.stacked) };
      update({ id: at.id, title: at.title, label: dropLabel(target, at.id, host, at.title), x: event.clientX, y: event.clientY, target });
    };
    const up = () => pending.current && finish(Boolean(pending.current.started));
    const key = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || !pending.current?.started) return;
      event.stopPropagation();
      finish(false);
    };
    const cancel = () => pending.current && !pending.current.touch?.held && finish(false);
    const held = () => Boolean(pending.current?.touch?.held);
    const press = (event: Event) => held() && event.preventDefault();
    const slide = (event: TouchEvent) => {
      if (!held()) return;
      event.preventDefault();
      if (event.touches[0]) move(event.touches[0]);
    };
    const lift = (event: TouchEvent) => held() && finish(event.type === 'touchend' && Boolean(pending.current?.started));
    touchHandlers.current = { slide, lift };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    window.addEventListener('blur', cancel);
    window.addEventListener('keydown', key, true);
    window.addEventListener('contextmenu', press);
    return () => {
      stopScrolling();
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      window.removeEventListener('blur', cancel);
      window.removeEventListener('keydown', key, true);
      window.removeEventListener('contextmenu', press);
    };
  }, [editor]);

  const start = (id: string, title: string, event: React.PointerEvent) => {
    if (!editor || editor.pending || event.button !== 0) return;
    const at: Pending = { id, title, x: event.clientX, y: event.clientY, started: false };
    if (event.pointerType === 'touch') {
      const release = new AbortController();
      const touched = event.target as HTMLElement;
      touched.addEventListener('touchmove', (moved) => touchHandlers.current?.slide(moved), { passive: false, signal: release.signal });
      touched.addEventListener('touchend', (ended) => touchHandlers.current?.lift(ended), { signal: release.signal });
      touched.addEventListener('touchcancel', (ended) => touchHandlers.current?.lift(ended), { signal: release.signal });
      at.touch = {
        held: false,
        release,
        timer: window.setTimeout(() => {
          if (pending.current !== at) return;
          at.touch!.held = true;
          update({ id, title, label: title, x: at.x, y: at.y });
        }, HOLD_MS),
      };
    }
    pending.current = at;
  };

  return { drag, start, attachPreview };
}
